/* eslint-disable @typescript-eslint/ban-types */
function memoizeImpl(): (target: any, propertyName: string, descriptor: PropertyDescriptor) => void {
    return (target, propertyName, descriptor) => {
        if (descriptor.value != null && Function.isFunction(descriptor.value)) {
            descriptor.value = makeMemoize(propertyName, descriptor.value);
        } else if (descriptor.get != null) {
            descriptor.get = makeMemoize(propertyName, descriptor.get);
        } else {
            throw new Error('Support only method or get property.');
        }
    };
}

type Result = { result: any } | { error: any };
const references = new WeakMap<object, Map<string, Result>>();

memoizeImpl.clear = function (instanceOrType: object, name: string, ...args: any[]) {
    const map = references.get(instanceOrType);
    if (map) {
        if (args.length === 0) {
            const keysForRemove = [...map.keys()].filter(k => k.startsWith(name + '$$'));
            for (const key of keysForRemove) {
                map.delete(key);
            }
        } else {
            map.delete(makeFunctionKey(name, args));
        }
    }
};

memoizeImpl.get = function (instanceOrType: object, name: string, ...args: any[]) {
    const map = references.get(instanceOrType);
    if (map) {
        const key = makeFunctionKey(name, args);
        return (map.get(key) as any).result;
    }
    return undefined;
};

type OnlyMethods<T> = Pick<
    T,
    {
        [TKey in keyof T]: T[TKey] extends (...args: any[]) => any ? TKey : never;
    }[keyof T]
>;

type Memoize = {
    (): <T extends (...args: any[]) => any>(
        target: object,
        propertyKey: string,
        descriptor: TypedPropertyDescriptor<T>
    ) => TypedPropertyDescriptor<T>;
    clear<T, FunName extends keyof OnlyMethods<T>>(type: T, name: FunName): void;
    clear<T, FunName extends keyof OnlyMethods<T>>(type: T, name: FunName, ...args: Parameters<OnlyMethods<T>[FunName]>): void;
    get<T1, FunName extends keyof OnlyMethods<T1>>(
        type: T1,
        name: FunName,
        ...args: Parameters<OnlyMethods<T1>[FunName]>
    ): ReturnType<OnlyMethods<T1>[FunName]> | undefined;
};

export const memoize = memoizeImpl as Memoize;

function makeFunctionKey(fnName: string, args: any[]): string {
    return fnName + '$$' + JSON.stringify(args);
}

function makeMemoize(fnIdentity: string, fn: (...args: any[]) => any): (...args: any[]) => any {
    return function (this: object, ...args: any[]): any {
        let cachedResults = references.get(this);
        if (!cachedResults) {
            cachedResults = new Map<string, any>();
            references.set(this, cachedResults);
        }

        const currentIdentity = makeFunctionKey(fnIdentity, args);

        if (cachedResults.has(currentIdentity)) {
            const result = cachedResults.get(currentIdentity) as Result;
            if ('error' in result) {
                throw result.error;
            }
            return result.result;
        } else {
            try {
                const result = fn.apply(this, args);
                cachedResults.set(currentIdentity, { result });
                return result;
            } catch (error) {
                cachedResults.set(currentIdentity, { error });
                throw error;
            }
        }
    };
}
